/**
 * ReviewedComment Component
 * Displays a reviewed (accepted/rejected/edited) comment with expand/collapse.
 */
import React, { useState, useEffect } from 'react';
import type { Comment } from '../../types';

interface ReviewedCommentProps {
  comment: Comment;
  isNew?: boolean;
}

const statusStyles = {
  accepted: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  edited: 'bg-blue-50 text-blue-700 border-blue-200',
  pending: 'bg-gray-50 text-gray-700 border-gray-200',
};

const statusIcons = {
  accepted: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  rejected: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  edited: (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  pending: null,
};

const ReviewedComment: React.FC<ReviewedCommentProps> = ({ comment, isNew = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPulse, setShowPulse] = useState(isNew);

  // Remove pulse animation after it plays
  useEffect(() => {
    if (showPulse) {
      const timer = setTimeout(() => setShowPulse(false), 300);
      return () => clearTimeout(timer);
    }
  }, [showPulse]);

  const truncatedText =
    comment.highlightedText.length > 50
      ? `${comment.highlightedText.slice(0, 50)}...`
      : comment.highlightedText;

  const statusStyle =
    statusStyles[comment.status as keyof typeof statusStyles] ||
    statusStyles.edited;

  const statusIcon = statusIcons[comment.status as keyof typeof statusIcons];

  return (
    <div
      className={`text-xs p-2 rounded border ${statusStyle} animate-fade-in transition-all ${
        showPulse ? 'animate-status-pulse' : ''
      }`}
    >
      {/* Header row with status and expand button */}
      <div className="flex items-center justify-between">
        <span className="font-medium capitalize flex items-center gap-1.5">
          {statusIcon}
          {comment.status}
        </span>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 hover:text-gray-700 px-1 text-sm font-mono"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? '−' : '+'}
        </button>
      </div>

      {/* Collapsed view */}
      {!isExpanded && <p className="mt-1">{truncatedText}</p>}

      {/* Expanded view */}
      {isExpanded && (
        <div className="mt-2 space-y-2">
          {/* Highlighted text */}
          <div>
            <span className="font-medium">Highlighted:</span>
            <p className="italic mt-1">"{comment.highlightedText}"</p>
          </div>

          {/* Original suggestion */}
          <div>
            <span className="font-medium">Suggestion:</span>
            <p className="mt-1">{comment.suggestion}</p>
          </div>

          {/* Final text for edited comments */}
          {comment.status === 'edited' && comment.finalText && (
            <div>
              <span className="font-medium">Edited to:</span>
              <p className="mt-1">{comment.finalText}</p>
            </div>
          )}

          {/* Rejection reason */}
          {comment.status === 'rejected' && comment.rejectionReason && (
            <div>
              <span className="font-medium">Reason:</span>
              <p className="mt-1">{comment.rejectionReason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewedComment;
